import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { GspTextType } from 'src/app/shared/common/components/gsp-text/gsp-text.component';
import { LayersStore } from 'src/app/shared/common/current-layers/layers-store.service';
import { LayersStreams } from 'src/app/shared/common/current-layers/layers-streams.service';
import { StylesStore } from 'src/app/shared/common/current-layers/styles-store.service';
import { MapWorkspacesStoreService } from 'src/app/shared/common/current-map-workspaces/map-workspaces-store.service';
import { GeometryTypes } from 'src/app/shared/common/utility/geometry-utils';
import { Layer } from 'src/app/shared/map-data-services/layer/layer';
import { LayerStyleGenerator } from 'src/app/shared/map-data-services/layer/layer-style-generator';
import { TemplateStatus } from 'src/app/shared/map-data-services/layer/template-lite';
import { Style } from 'src/app/shared/map-data-services/styles/style';
import { LayerSymbologyType, SymbologyOverrideChoice } from 'src/app/shared/map-data-services/styles/style-symbology';
import { StyleSymbologyStreamService } from 'src/app/shared/map-data-services/styles/style-symbology-stream.service';
import { Field, FieldType } from 'src/app/shared/template-services/field';
import { Template } from 'src/app/shared/template-services/template';
import { UserMapWorkspaceSetting } from 'src/app/shared/user/user-map-workspace-settings';
import { UserSettingsStreamService } from 'src/app/shared/user/user-settings-stream.service';
import { SymbologyPanelComponent } from '../../template-editor/style-symbology-panel/style-symbology-panel.component';
import { TemplateStoreForComponent } from '../../template-editor/template/template-store-for-component.service';
import { MapContentMonitor } from '../common/map-content-monitor.service';
import { LabelDropdownOptions } from './label';

@Component({
    templateUrl: 'edit-layer.component.html',
    selector: 'edit-layer'
})
export class EditLayerComponent implements OnInit, OnDestroy {
    @ViewChild(SymbologyPanelComponent) styleSymbologyPanel: SymbologyPanelComponent;

    public disableSave = true;
    public originalLayer: Layer;
    public layer: Layer;
    public originalTemplate: Template;
    public template: Template;
    public originalTemplateStyle: Style;
    public templateStyle: Style;

    // expose ButtonType enum to template
    public ButtonType = ButtonType;
    public GspTextType = GspTextType;
    public GeometryTypes = GeometryTypes;

    // Label settings
    public labelOptions: LabelDropdownOptions[] = [];
    public selectedLabelOption: LabelDropdownOptions['name'] = '';
    private userWorkspaceSettings: UserMapWorkspaceSetting;

    private destroyed = new Subject<void>();

    public loading = false;

    constructor(
        private layersStreams: LayersStreams,
        private layersStore: LayersStore,
        private templateStoreForComponent: TemplateStoreForComponent,
        private styleSymbologyStream: StyleSymbologyStreamService,
        private stylesStore: StylesStore,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private mapContentMonitor: MapContentMonitor,
        private messaging: MessagingService,
        private translate: TranslationService,
        private userSettingsStreamService: UserSettingsStreamService,
        private mapWorkspacesStoreService: MapWorkspacesStoreService,
        private cdr: ChangeDetectorRef
    ) {}

    public async ngOnInit(): Promise<void> {
        const layer = this.layersStreams.currentEditLayerStream.getValue();
        const workspace = this.mapWorkspacesStoreService.getCurrentMapWorkspace();

        this.loading = true;

        this.userWorkspaceSettings = await this.userSettingsStreamService.getWorkspaceSettings(workspace.id);

        if (layer.templateId) {
            this.template = await this.templateStoreForComponent.getTemplateById(layer.projectId, layer.templateId);
        } else {
            this.template = new Template();
            this.template.geometryType = layer.geometryType;
            this.templateStyle = layer.style;
        }
        this.originalTemplate = _.cloneDeep(this.template);

        await this.stylesStore.getStyle(layer.projectId, layer.styleId).then(style => {
            this.templateStyle = _.cloneDeep(style);
            this.originalTemplateStyle = _.cloneDeep(style);
        });

        this.layersStreams.currentEditLayerStream.pipe(takeUntil(this.destroyed)).subscribe(layer => {
            if (layer && layer.id) {
                this.originalLayer = _.cloneDeep(layer);
                this.layer = _.cloneDeep(layer);
            } else if (this.activatedRoute.snapshot.queryParamMap.get('layerId')) {
                this.layersStore
                    .getMapWorkspaceLayerByIdPromise(this.activatedRoute.snapshot.queryParamMap.get('layerId'))
                    .then(newLayer => {
                        this.originalLayer = _.cloneDeep(newLayer);
                        this.layer = _.cloneDeep(newLayer);
                    });
            }

            this.initializeLabelSettings();
            this.loading = false;
            this.disableSave = false;

            // detect all async/await functions, make sure to detect changes - reference: https://angular.dev/errors/NG0100
            this.cdr.detectChanges();
        });
    }

    public ngOnDestroy(): void {
        this.destroyed.next(null);
    }

    public getErrorText(errors: object): string {
        const isRequiredError = _.filter([errors], 'required');
        if (isRequiredError.length) {
            return this.translate.instant('RequiredError');
        }
        return null;
    }

    public isSaveDisabled(): boolean {
        const isCustomTextEmpty =
            this.templateStyle?.symbol.type === LayerSymbologyType.CUSTOMIZABLE &&
            !this.templateStyle?.symbol.text.length;

        const isOverridesEmpty = this.template?.enableAttributedSymbols && !this.templateStyle?.styleOverrides?.length;

        const isUnchanged =
            _.isEqual(this.originalLayer, this.layer) &&
            _.isEqual(this.originalTemplateStyle, this.styleSymbologyPanel?.templateStyle) &&
            _.isEqual(this.originalTemplate, this.styleSymbologyPanel?.template) &&
            _.isEqual(this.selectedLabelOption, this.userWorkspaceSettings.layerIdLabelMap?.[this.layer.id] || '');

        const isLayerNameEmpty = !this.layer?.layerName;

        return (
            !this.userWorkspaceSettings ||
            this.disableSave ||
            isCustomTextEmpty ||
            isOverridesEmpty ||
            isUnchanged ||
            isLayerNameEmpty
        );
    }

    public async saveLayer(): Promise<void> {
        const layer = this.layer;

        const previousStyleOverrides = this.originalTemplateStyle.styleOverrides as SymbologyOverrideChoice[];
        const currentTemplateStyle = _.cloneDeep(this.styleSymbologyPanel.templateStyle);
        const currentTemplate = _.cloneDeep(this.styleSymbologyPanel.template);

        this.updateLayerIdLabelMap();

        let currentOverrides = this.templateStyle.styleOverrides as SymbologyOverrideChoice[];

        if (!currentTemplate.enableAttributedSymbols) {
            currentOverrides = [];
        } else if (!currentTemplateStyle.styleOverrides.length) {
            currentTemplate.enableAttributedSymbols = false;
        }

        currentTemplateStyle.symbolId = currentTemplateStyle.symbol.id;

        if (currentTemplate.geometryType === GeometryTypes.LINE) {
            currentTemplate.geometryColorHexRGB = LayerStyleGenerator.decimalToHexRgb(currentTemplateStyle.lineColor);
        } else {
            if (currentTemplateStyle.symbol.type === LayerSymbologyType.CUSTOMIZABLE) {
                currentTemplate.geometryColorHexRGB = LayerStyleGenerator.decimalToHexRgb(
                    currentTemplateStyle.symbol.color
                );
            } else {
                currentTemplate.geometryColorHexRGB = LayerStyleGenerator.decimalToHexRgb(
                    currentTemplateStyle.symbol.fillColor
                );
            }
        }

        layer.color = currentTemplate.geometryColorHexRGB;

        this.disableSave = true;
        this.loading = true;

        try {
            const style = currentTemplateStyle;

            const newLayer = this.layer.toDTO();
            newLayer.styleId = style.id;
            currentOverrides = await this.styleSymbologyStream.createOrUpdateSymbologyOverrides(
                style.id,
                previousStyleOverrides,
                currentOverrides
            );
            currentTemplateStyle.styleOverrides = currentOverrides;

            await this.stylesStore.updateStyle(
                layer.projectId,
                style.id,
                style,
                currentTemplate.geometryType,
                currentTemplate.enableSymbols
            );
            if (this.template.id) {
                const updatedTemplate = await this.templateStoreForComponent.updateTemplate(
                    currentTemplate,
                    currentTemplate.status === TemplateStatus.PUBLISHED,
                    layer.projectId
                );
                newLayer.templateId = updatedTemplate.id;
            }

            await this.layersStore.updateLayer(layer.projectId, newLayer.id, newLayer, currentTemplateStyle);

            this.messaging.showSuccess(this.translate.instant('TC.Common.StyleSaveSuccessful'));
        } catch (error) {
            this.messaging.showError(this.translate.instant('TC.Common.StyleSaveFailed'));
        } finally {
            this.loading = false;
            this.mapContentMonitor.refreshMapContentStream.next(true);
            this.closeEditPanel();

            // detect all async/await functions, make sure to detect changes - reference: https://angular.dev/errors/NG0100
            this.cdr.detectChanges();
        }
    }

    public closeEditPanel(): void {
        this.router.navigate(['mapViewer'], { queryParams: { layerId: null }, queryParamsHandling: 'preserve' });
    }

    private initializeLabelSettings() {
        // skips label initialization if no geometry type is NONE
        if (this.layer?.geometryType === GeometryTypes.NONE) return;

        const layerId = this.layer.id || this.activatedRoute.snapshot.queryParamMap.get('layerId');
        const layerFields = this.layersStreams.cachedLayerFields[layerId];

        const noneOption = {
            id: null,
            displayName: 'TC.Common.None',
            name: ''
        } as LabelDropdownOptions;

        // if layerfield value is falsy, reset label options and selected option
        if (!layerFields || !layerFields?.length) {
            this.labelOptions = [noneOption];
            this.selectedLabelOption = '';
            return;
        }

        // translate layerFields to LabelDropdownOptions
        const populatedOptions = this.fieldToLabelOptions(layerFields as Field[]);

        this.labelOptions = [noneOption, ...populatedOptions];

        // retrieve the layerIdLabelMap from userWorkspaceSettings
        if (this.userWorkspaceSettings?.layerIdLabelMap) {
            const selectedName = this.userWorkspaceSettings?.layerIdLabelMap[layerId] as string;
            this.selectedLabelOption = this.labelOptions.find(option => option.name === selectedName)?.name ?? '';
        }
    }

    private updateLayerIdLabelMap(): void {
        if (!this.layer) {
            return;
        }

        const layerId = this.layer.id;
        const labelName = this.selectedLabelOption;

        this.userWorkspaceSettings.layerIdLabelMap = {
            ...this.userWorkspaceSettings.layerIdLabelMap,
            [layerId]: labelName || null
        };

        // if labelName is falsy, remove the layerId from layerIdLabelMap
        if (!labelName) {
            delete this.userWorkspaceSettings.layerIdLabelMap[layerId];
        }

        this.userSettingsStreamService.updateCurrentWorkspaceSettings(this.userWorkspaceSettings);
    }

    private fieldToLabelOptions(fields: Field[]): LabelDropdownOptions[] {
        const disallowedTypes = [FieldType.Image.toLowerCase(), FieldType.Signature.toLowerCase()];
        const remappedFields = fields
            .filter(field => !disallowedTypes.some(type => field.name.includes(type)))
            .map((field: Field) => {
                return {
                    id: field.uuid,
                    displayName: field.displayName,
                    name: field.name
                } as LabelDropdownOptions;
            });

        return remappedFields;
    }
}
